// withPlayerTrackingEvent.js
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import getAssetData from '../selector/getAssetData.js';
import getLiveStreamData from '../selector/getLiveStreamData.js';
import getMessageData from '../selector/getMessageData.js';
import getOperationData from '../selector/getOperationData.js';
import trackMixpanel, { EventTypes, getViewId } from './mixpanel.js';
import getWidevineInfo from './getWidevineInfo.js';

const { PLAYER_VISIBLE, PLAYER_STARTED, PLAYER_STOPPED, PLAYER_HIDDEN } =
  EventTypes;

export const VIDEO_CATEGORY = {
  FLIX: 'flix',
  POST: 'post',
  SHORTS: 'shorts',
  STORY: 'story',
  LIVESTREAM: 'livestream',
  LIVESTREAM_DASH: 'livestream_dash',
};

function withPlayerTrackingEvent(WrappedComponent, category) {
  const isLiveStream = [
    VIDEO_CATEGORY.LIVESTREAM,
    VIDEO_CATEGORY.LIVESTREAM_DASH,
  ].includes(category);

  class PlayerTrackingEvent extends React.Component {
    wrappedComponentRef = React.createRef();
    durationMap = new Map();

    trackPlayerEventAsVisibilityChanged = ({ isBrowserTabVisible }) => {
      const timestamp = Date.now();
      if (isBrowserTabVisible) {
        this.trackPlayerEvent({
          type: EventTypes.PLAYER_VISIBLE,
          timestamp,
        });
      } else {
        this.trackPlayerEvent({
          type: EventTypes.PLAYER_HIDDEN,
          timestamp,
          shouldSendBeacon: true,
        });
      }
    };

    trackPlayerEvent = async ({
      type,
      timestamp,
      shouldSendBeacon = false,
      prevProps, // used by Story
      resourceUri,
    }) => {
      const {
        location,
        disableTrackPlayingTime,
        isPrivateStreamUnlocked,
        streamStatus,
        streamId,
        messageId,
        postId,
        assetId,
        assetIds,
        senderId,
        senderName,
        senderUsername,
        badges,
        mediaType,
        canViewMedia,
        isUnlocked,
      } = prevProps || this.props;

      if (disableTrackPlayingTime) return;
      if (
        ![
          PLAYER_VISIBLE,
          PLAYER_STARTED,
          PLAYER_STOPPED,
          PLAYER_HIDDEN,
        ].includes(type)
      ) {
        return;
      }
      if ('production' !== process.env.NODE_ENV && PLAYER_STARTED === type) {
        const player = this.wrappedComponentRef?.current?.player;
        if (!player) {
          throw new ReferenceError(`
            The "player" is not well set in wrapped component.
            If your component is class component, please set "this.player";
            if your component is function component, please leverage "useImperativeHandle" to expose player.
          `);
        }
      }

      const viewId = getViewId({
        pathname: location.pathname || this.pathname,
      });
      // Take Shorts for example,
      // the first URL would be replaced from '/shorts' to '/shorts/:messageId',
      // that's why we add this to filter that meanless tracking.
      if (!viewId) return;

      const messageStatus = canViewMedia || isUnlocked ? 'unlocked' : 'locked';
      const resource = resourceUri || this.getResource();
      const { securityLevel, hdcpStatus } = await getWidevineInfo({
        keySystem: 'com.widevine.alpha',
      });
      let extraProps = {
        'user.username': senderName || senderUsername,
        'user.id': senderId || streamId,
        'media.type': isLiveStream
          ? 'video'
          : 0 === mediaType.search('video')
          ? 'video'
          : 'image',
        resource,
      };
      if (isLiveStream) {
        extraProps = {
          ...extraProps,
          'stream.status': streamStatus,
          'stream.unlocked': isPrivateStreamUnlocked,
        };
      } else {
        extraProps = {
          ...extraProps,
          'asset.id': assetId || assetIds[0],
          'message.status': messageStatus,
          'widevine.security.level': securityLevel,
          'hdcp.2.2.status': hdcpStatus,
          badges,
        };
      }
      const payload = {
        viewId,
        shouldSendBeacon,
        extraProps,
      };
      if (isLiveStream) {
        payload.streamId = streamId;
      } else {
        payload.messageId = messageId || postId;
      }

      let playedDuration = 0;
      if (type === PLAYER_HIDDEN) {
        if (this.durationMap.has(PLAYER_VISIBLE)) {
          playedDuration = Math.floor(
            (timestamp - this.durationMap.get(PLAYER_VISIBLE)) / 1000
          );
          this.durationMap.delete(PLAYER_VISIBLE);
        } else {
          return;
        }
        payload.extraProps.duration = playedDuration;
      } else if (type === PLAYER_STOPPED) {
        if (this.durationMap.has(resource)) {
          playedDuration = Math.floor(
            (timestamp - this.durationMap.get(resource)) / 1000
          );
          this.durationMap.delete(resource);
        } else {
          // STORY:
          // - Happened when (messageId changed && (video ended sent || image timer over)).
          // - It would/should not enter here if users change messages via click/keydown/touch.
          return;
        }
        payload.extraProps.duration = playedDuration;
      } else if (type === PLAYER_STARTED) {
        if (this.durationMap.has(resource)) {
          // For something like bad connection. Let's just record the first one.
          return;
        } else {
          this.durationMap.set(resource, timestamp);
        }
      } else if (type === PLAYER_VISIBLE) {
        if (this.durationMap.has(PLAYER_VISIBLE)) {
          // Just in case. Shouldn't meet this condition though.
          await this.trackPlayerEvent({ type: PLAYER_HIDDEN, timestamp });
        }
        this.durationMap.set(PLAYER_VISIBLE, timestamp);
      }

      trackMixpanel({ type, payload });
    };

    getResource = () => {
      const player = this.wrappedComponentRef?.current?.player;
      if (!player) return '';
      let resource;
      if (category === VIDEO_CATEGORY.LIVESTREAM_DASH) {
        // try-catch is necessary for dashjs' getSource()
        try {
          resource = player?.getSource?.();
        } catch (e) {
          resource = '';
        }
      } else if (category === VIDEO_CATEGORY.LIVESTREAM) {
        resource = player?.currentSrc?.();
      } else {
        resource = player?.getAssetUri?.();
      }
      return resource;
    };

    componentDidMount() {
      // This is for those which don't have `location` prop, like `Feed`.
      this.pathname = window.location.pathname;
    }

    componentDidUpdate(prevProps) {
      const player = this.wrappedComponentRef?.current?.player;
      if (!player) return;

      const { isBrowserTabVisible } = this.props;
      if (isBrowserTabVisible !== prevProps.isBrowserTabVisible) {
        this.trackPlayerEventAsVisibilityChanged({ isBrowserTabVisible });
      }
    }

    render() {
      return (
        <WrappedComponent
          ref={this.wrappedComponentRef}
          {...this.props}
          trackPlayerEvent={this.trackPlayerEvent}
        />
      );
    }

    componentWillUnmount() {
      const player = this.wrappedComponentRef?.current?.player;
      if (player) {
        // In case that the player is released earlier, we pass `resourceUri` manually ASAP.
        const resourceUri = this.getResource();
        const timestamp = Date.now();
        this.trackPlayerEvent({
          type: EventTypes.PLAYER_STOPPED,
          resourceUri,
          timestamp,
        });
        this.trackPlayerEvent({
          type: EventTypes.PLAYER_HIDDEN,
          resourceUri,
          timestamp,
        });
      }
    }
  }

  PlayerTrackingEvent.propTypes = {
    location: PropTypes.object,
    isBrowserTabVisible: PropTypes.bool,
    isPrivateStreamUnlocked: PropTypes.bool,
    streamStatus: PropTypes.string,
    streamId: PropTypes.string,
    messageId: PropTypes.string,
    postId: PropTypes.string, // Feed uses it
    assetId: PropTypes.string,
    assetIds: PropTypes.array, // Shorts uses it
    senderId: PropTypes.string,
    senderName: PropTypes.string,
    senderUsername: PropTypes.string, // Shorts uses it as `senderName`
    canViewMedia: PropTypes.bool,
    isUnlocked: PropTypes.bool, // Story uses it
    disableTrackPlayingTime: PropTypes.bool,
    mediaType: PropTypes.string,
    badges: PropTypes.array,
  };

  PlayerTrackingEvent.defaultProps = {
    location: {},
    isBrowserTabVisible: true,
    isPrivateStreamUnlocked: false,
    streamStatus: '',
    streamId: '',
    messageId: '',
    postId: '',
    assetId: '',
    assetIds: [],
    senderId: '',
    senderName: '',
    senderUsername: '',
    canViewMedia: null,
    isUnlocked: false,
    disableTrackPlayingTime: false,
    mediaType: '',
    badges: [],
  };

  const mapStateToProps = (
    state,
    { assetId, assetIds, messageId, streamId }
  ) => {
    const usingAssetId = assetId || assetIds?.[0];
    const newProps = {
      VIDEO_CATEGORY,
      mediaType: usingAssetId
        ? getAssetData(state, [usingAssetId], 'contentType')
        : getMessageData(state, messageId, 'mediaType'),
      isBrowserTabVisible: getOperationData(
        state,
        ['visibility'],
        'isBrowserTabVisible'
      ),
    };
    return isLiveStream
      ? {
          ...newProps,
          streamStatus: getLiveStreamData(state, [streamId], 'status'),
          isPrivateStreamUnlocked: getOperationData(
            state,
            ['liveStream', 'paid', streamId],
            'isPrivateStreamUnlocked'
          ),
        }
      : newProps;
  };

  return connect(mapStateToProps)(PlayerTrackingEvent);
}

export default withPlayerTrackingEvent;
